// Sender Identifier / IdentifierCode on a received XREF01 (and XREF02).
// Screen and print show the MedCom type as the row heading (Ydernummer, …) and the number alone as the value.

/** Official MedCom IdentifierCodeType (ValueLists.xsd) plus common SOR spellings. */
const SOR_IDENTIFIER_CODES = new Set(['sorkode', 'sor', 'sor-kode']);

export function trimIdentifierPart(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** True only when IdentifierCode is a MedCom SOR code (`sorkode`, `sor`, `SOR-kode`). */
export function isSorIdentifierCode(code: string | null | undefined): boolean {
  const raw = trimIdentifierPart(code);
  if (!raw) return false;
  return SOR_IDENTIFIER_CODES.has(raw.toLowerCase().replace(/_/g, '-'));
}

/**
 * Value shown beside {@link senderIdentifierHeadingKey}: the identifier only.
 * The MedCom type (ydernummer, sygehusafdelingsnummer, …) belongs in the heading.
 */
export function formatMedcomIdentifier(
  identifier: string | null | undefined,
  _identifierCode?: string | null | undefined,
): string | null {
  return trimIdentifierPart(identifier);
}

export type MedcomIdentifierDisplay = {
  headingKey: IdentifierHeadingKey;
  value: string;
};

/**
 * Heading + value for Sender, Referrer, Receiver and CCReceiver on screen and print.
 * When IdentifierCode is missing on Referrer, `fallbackIdentifierCode` (e.g. Sender's code) is used.
 */
export function medcomIdentifierDisplay(
  identifier: string | null | undefined,
  identifierCode: string | null | undefined,
  options?: { fallbackIdentifierCode?: string | null | undefined },
): MedcomIdentifierDisplay | null {
  const value = trimIdentifierPart(identifier);
  if (!value) return null;
  const code =
    trimIdentifierPart(identifierCode) ?? trimIdentifierPart(options?.fallbackIdentifierCode);
  return { headingKey: senderIdentifierHeadingKey(code), value };
}

/** True when IdentifierCode is MedCom `ydernummer`. */
export function isYdernummerIdentifierCode(code: string | null | undefined): boolean {
  return trimIdentifierPart(code)?.toLowerCase() === 'ydernummer';
}

/** True when IdentifierCode is MedCom `sygehusafdelingsnummer`. */
export function isSygehusafdelingsnummerIdentifierCode(code: string | null | undefined): boolean {
  return trimIdentifierPart(code)?.toLowerCase() === 'sygehusafdelingsnummer';
}

export type IdentifierHeadingKey =
  | 'referrals.field_referring_doctor_sor'
  | 'referrals.identifier_ydernummer'
  | 'referrals.medcom_identifier_sygehusafdelingsnummer'
  | 'referrals.xref01_identifier';

/**
 * Heading for an Identifier cell (sender, kopimodtager).
 * SOR-nummer only when the code really is SOR, Ydernummer when it is a
 * ydernummer (REF06-M 3.13, MedCom review G4/G5), else Identifikation.
 * Missing IdentifierCode falls back to Identifikation, never SOR-nummer.
 */
export function senderIdentifierHeadingKey(identifierCode: string | null | undefined): IdentifierHeadingKey {
  if (isSorIdentifierCode(identifierCode)) return 'referrals.field_referring_doctor_sor';
  if (isYdernummerIdentifierCode(identifierCode)) return 'referrals.identifier_ydernummer';
  if (isSygehusafdelingsnummerIdentifierCode(identifierCode)) return 'referrals.medcom_identifier_sygehusafdelingsnummer';
  return 'referrals.xref01_identifier';
}

export type SenderIdentity = {
  identifier: string | null;
  identifierCode: string | null;
};

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Sender Identifier/IdentifierCode persisted on medcom_details (XREF01/XREF02). */
export function readStoredSenderIdentity(
  details: Record<string, unknown> | null | undefined,
): SenderIdentity {
  const sender = isPlainRecord(details) && isPlainRecord(details.sender) ? details.sender : null;
  return {
    identifier: trimIdentifierPart(typeof sender?.identifier === 'string' ? sender.identifier : null),
    identifierCode: trimIdentifierPart(
      typeof sender?.identifierCode === 'string' ? sender.identifierCode : null,
    ),
  };
}

/**
 * Prefer medcom_details.sender (has IdentifierCode). Fall back to the legacy
 * referring_doctor_sor column so older imports still show the number.
 */
export function referralSenderIdentity(referral: {
  referring_doctor_sor: string | null;
  medcom_details: Record<string, unknown>;
}): SenderIdentity {
  const stored = readStoredSenderIdentity(referral.medcom_details);
  return {
    identifier: stored.identifier ?? trimIdentifierPart(referral.referring_doctor_sor),
    identifierCode: stored.identifierCode,
  };
}
